//@ts-check
import {useCallback, useEffect, useState} from 'react'
import {reorderCatalogItems, reorderCategorySubcategories, sortCatalogData} from '../utils/productCatalogUtils'
import {useProductCatalogData} from './useProductCatalogData'

/**
 * @returns {{
 *   brands: import('@/types/productCatalogTypes').ProductCategoryBrand[],
 *   categories: import('@/types/productCatalogTypes').ProductCategory[],
 *   isLoading: boolean,
 *   setBrands: import('react').Dispatch<import('react').SetStateAction<import('@/types/productCatalogTypes').ProductCategoryBrand[]>>,
 *   setCategories: import('react').Dispatch<import('react').SetStateAction<import('@/types/productCatalogTypes').ProductCategory[]>>,
 *   handleBrandsReorder: (event: import('@dnd-kit/dom').DragEndEvent) => void,
 *   handleCategoriesReorder: (event: import('@dnd-kit/dom').DragEndEvent) => void,
 *   handleSubcategoriesReorder: (categoryId: string, event: import('@dnd-kit/dom').DragEndEvent) => void,
 *   toggleSubcategoryVisibility: (categoryId: string, subcategoryId: string) => void,
 * }}
 */
export const useProductCatalog = () => {
  const {data, isLoading} = useProductCatalogData()
  const [brands, setBrands] = useState(/** @type {import('@/types/productCatalogTypes').ProductCategoryBrand[]} */ ([]))
  const [categories, setCategories] = useState(
    /** @type {import('@/types/productCatalogTypes').ProductCategory[]} */ ([])
  )

  useEffect(() => {
    if (!data) {
      return
    }

    const sortedCatalogData = sortCatalogData(data)

    setBrands(sortedCatalogData.brands) // Temporary fix to limit brands to 6 until pagination is implemented
    setCategories(sortedCatalogData.categories) // Temporary fix to limit categories to 30 until pagination is implemented
  }, [data])

  const handleBrandsReorder = useCallback(event => {
    setBrands(prev => reorderCatalogItems(prev, event))
  }, [])

  const handleCategoriesReorder = useCallback(event => {
    setCategories(prev => reorderCatalogItems(prev, event))
  }, [])

  const handleSubcategoriesReorder = useCallback((categoryId, event) => {
    setCategories(prev => reorderCategorySubcategories(prev, categoryId, event))
  }, [])

  const toggleSubcategoryVisibility = useCallback((categoryId, subcategoryId) => {
    setCategories(prev =>
      prev.map(category =>
        category.id === categoryId
          ? {
              ...category,
              subCategories: category.subCategories.map(sub =>
                sub.id === subcategoryId ? {...sub, visible: !sub.visible} : sub
              ),
            }
          : category
      )
    )
  }, [])

  return {
    brands,
    categories,
    isLoading,
    setBrands,
    setCategories,
    handleBrandsReorder,
    handleCategoriesReorder,
    handleSubcategoriesReorder,
    toggleSubcategoryVisibility,
  }
}
